import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  Inject,
  OnInit,
  OnChanges,
  OnDestroy,
  ElementRef,
  HostBinding,
  Renderer2,
  RendererStyleFlags2,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit,
  ViewChildren,
  QueryList,
  Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TypeOfPager, TypePositionOfArrow } from 'cms/cms-shared/common-settings-styles/common-settings-styles.models';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { BorderRadiusType, ImageLocation, PieceProductCarouselSettings, PieceProductCarouselSource, PieceProductCarouselViewStyle } from 'extensions/core/piece-product-carousel/front/models/piece-product-carousel.model';
import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { MecacoService } from 'idt-ecommerce-api/catalog/mecaco/mecaco.service';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { Product, ProductWithCategory } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { RenderSetupDataService } from 'idt-ecommerce-api/render-setup-data/render-setup-data.service';
import { CMSEvent, CMSOnLoadProductsEvent, CMSProductCarouselEvent } from 'render/models/cms-event.model';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { selectIsModalOpen } from 'render/render-shared/ui/render-modal-panel/store/render-modal-panel.state';
import { getStringParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { RenderStorageProductsService } from 'render/render-storage/render-storage-products/render-storage-products.service';
import { RenderState } from 'render/render.state';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { Observable, BehaviorSubject, of, iif, combineLatest } from 'rxjs';
import {
  switchMap, distinctUntilChanged, filter, map, takeUntil, tap
} from 'rxjs/operators';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { InditexWindow } from 'shared/models/inditex-window.models';
import { AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';
import { SwiperContainer } from 'swiper/element';
import { Autoplay, Grid, Pagination, Scrollbar, A11y, FreeMode } from 'swiper/modules';
import { PaginationOptions } from 'swiper/types/modules/pagination';
import { SwiperOptions } from 'swiper/types/swiper-options';

import {
  PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS,
  PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS,
  PRODUCT_CAROUSEL_DEFAULT_PRODUCT_NAME_FORMAT
} from 'extensions/st/piece-product-carousel-recommended/front/piece-product-carousel-recommended.definition';

import { styleUrls, templateUrl } from './piece-product-carousel-recommended-render.component.paths';


const IPOC_PRODUCTS_INITIALIZED = 'arrayProductsChanged';
const PRODUCT_IMAGE_CLASS = 'product-single';
const EXTERNAL_SOURCES: PieceProductCarouselSource[] = ['externalRecommendations', 'yodaRecommendedForYou', 'yodaTrends'];
// TODO Remove support for categoryV2 https://axinic.central.inditex.grp/jira/browse/CMS-7284
const CATEGORIES_SOURCES: PieceProductCarouselSource[] = ['category', 'categoryV2', 'seoId'];


@Component({
  selector: 'piece-product-carousel-recommended-render',
  templateUrl,
  styleUrls,
  encapsulation: ViewEncapsulation.ShadowDom
})

export class PieceProductCarouselRecommendedRenderComponent
  extends BaseRenderComponent<PieceProductCarouselSettings>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @ViewChild('swiper') public swiperContainer?: ElementRef<SwiperContainer>;
  @ViewChildren('swiperSlide') public swiperSlides!: QueryList<ElementRef>;
  @HostBinding('style.zIndex') public zIndex = '0';

  public displayComponentArrows: boolean = false;
  public displayComponentPager: boolean = false;
  public sourceType: PieceProductCarouselSource = 'category';
  public readonly productsInWishlist: Product['id'][] = [];

  public sourceType$ = new BehaviorSubject<PieceProductCarouselSource>(this.sourceType);
  public sourceId$ = new BehaviorSubject<string | null>(null);

  public products$!: Observable<Product[] | ProductWithCategory[] | undefined>;

  public imageClass = PRODUCT_IMAGE_CLASS;

  public swiperConfig: SwiperOptions = {};

  public provider: string = 'none';
  public strategy: string = 'manual';
  public objective: string = 'control';

  private displayedProductIds: string[] = [];
  private currency: string = '';
  private lastFocusedElement: HTMLElement | null = null;
  private isImageFocused: boolean = false;

  private readonly configs: Signal<GrowthbookConfig[]> = toSignal(
    this.renderGrowthbookService.configurations$,
    { initialValue: [] as GrowthbookConfig[] }
  );

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly elementRef: ElementRef,
    private readonly productsService: ProductsService,
    private readonly mecacoService: MecacoService,
    private readonly contextService: ContextService,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly renderer: Renderer2,
    private readonly localStorageService: RenderStorageProductsService,
    private readonly renderEventsService: RenderEventsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService,
    private readonly renderSetupDataService: RenderSetupDataService,
    private readonly store: Store<RenderState>
  ) {
    super();
  }

  public get imageBorderRadiusType(): BorderRadiusType {
    return this.piece?.settings?.styles?.imageBorderRadiusType ?? 'straight';
  }

  public get imageBorderRadius(): string {
    return this.piece?.settings?.styles?.imageBorderRadius ?? '0pt';
  }

  public get scrollMode(): 'free' | 'pager' {
    return this.piece?.settings?.general?.scrollMode ?? 'free';
  }

  @HostBinding('class.scroll-free')
  public get isScrollFree() {
    return this.scrollMode === 'free';
  }

  @HostBinding('class.scroll-pager')
  public get isScrollPager() {
    return this.scrollMode === 'pager';
  }

  public get viewStyle(): PieceProductCarouselViewStyle {
    return this.piece?.settings?.general?.viewStyle ?? 'grid-image';
  }

  @HostBinding('class.view-style-grid')
  public get isViewStyleGrid() {
    return this.viewStyle === 'grid-image';
  }

  @HostBinding('class.view-style-silhouette')
  public get isViewStyleSilhouette() {
    return this.viewStyle === 'silhouette-image';
  }

  @HostBinding('class.view-style-double')
  public get isViewStyleDouble() {
    return this.viewStyle === 'double-image';
  }

  public get pagerStyle(): TypeOfPager {
    return this.piece?.settings?.general?.pagerStyle ?? 'dot';
  }

  public get arrowPosition(): TypePositionOfArrow {
    return this.piece?.settings?.general?.arrowPosition ?? 'inside-left-right';
  }

  @HostBinding('class.pager-style-dot')
  public get isPagerStyleDot() {
    return this.pagerStyle === 'dot';
  }

  @HostBinding('class.pager-style-box')
  public get isPagerStyleBox() {
    return this.pagerStyle === 'box';
  }

  @HostBinding('class.pager-style-solid')
  public get isPagerStyleSolid(): boolean {
    return this.pagerStyle === 'solid';
  }

  @HostBinding('class.pager-style-dashed')
  public get isPagerStyleDashed(): boolean {
    return this.pagerStyle === 'dashed';
  }

  @HostBinding('class.dark-theme')
  public get isDarkTheme() {
    return this.piece?.settings?.general?.pagerStyleTheme === 'dark-theme';
  }

  @HostBinding('class.light-theme')
  public get isLightTheme() {
    return this.piece?.settings?.general?.pagerStyleTheme === 'light-theme';
  }

  @HostBinding('style.--color-product-name')
  public get colorProductName(): string {
    return getStringParam(this.configs(), 'content.home-carrusel.primary-color', '#000000');
  }

  public get isFirstSlide(): boolean {
    return !!this.swiperContainer?.nativeElement.swiper.isBeginning && !this.piece?.settings?.general.loop;
  }

  public get isLastSlide(): boolean {
    return !!this.swiperContainer?.nativeElement.swiper.isEnd && !this.piece?.settings?.general.loop;
  }

  public get maxProducts(): number | undefined {
    return Number(this.piece?.settings?.general?.maxProducts) || undefined;
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public ngOnInit(): void {
    this.initConfig();
    this.initTranslate();

    this.contextService.getStore$().pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe((store) => {
      this.currency = store.details.currencyInfo?.currencyCode ?? '';
    });

    this.window.inditex?.onReady?.(() => {
      this.window.inditex!.notifications.addHandler(IPOC_PRODUCTS_INITIALIZED, () => {
        if (EXTERNAL_SOURCES.includes(this.sourceType)) {
          this.sourceType$.next(this.sourceType);
          this.window.inditex!.notifications.clearHandlers(IPOC_PRODUCTS_INITIALIZED);
        }
      });
    });

    const productsFromCategory$ = this.sourceId$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      switchMap(
        (categoryId) => this.productsService.categoryV3Products(
          categoryId, true, this.maxProducts, this.configs()
        )
      )
    );

    const productsFromBundle$ = this.sourceId$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      switchMap((productId) => {
        return this.productsService.productDetailOrNull(productId).pipe(
          map((product) => (isDefined(product) ? product.bundleProducts : []))
        );
      })
    );

    const productsFromList$ = this.sourceId$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      map((sourceId) => this.sanitizeProducts(sourceId)),
      switchMap(
        (productIds: string) => this.productsService.productsDetailArray(
          productIds, undefined, undefined, this.configs()
        )
      )
    );

    const productsFromMecaco$ = this.sourceId$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      switchMap((sourceId: string) => this.mecacoService.getMecacoProducts(sourceId, this.maxProducts))
    );

    this.products$ = this.sourceType$.pipe(
      takeUntil(this.clearSubscriptions$),
      filter(isDefined),
      distinctUntilChanged(
        (prev: PieceProductCarouselSource, next: PieceProductCarouselSource) => prev === next
          && !EXTERNAL_SOURCES.some((source) => [prev, next].includes(source))
      ),
      switchMap((sourceType) => {
        this.sourceType = sourceType;
        // TODO Remove support for categoryV2 https://axinic.central.inditex.grp/jira/browse/CMS-7284
        if (CATEGORIES_SOURCES.includes(sourceType)) {
          return productsFromCategory$;
        }

        if (sourceType === 'productBundle') {
          return productsFromBundle$;
        }

        if (sourceType === 'productList') {
          return productsFromList$;
        }

        if (EXTERNAL_SOURCES.includes(sourceType)) {
          return this.externalProducts();
        }

        if (sourceType === 'localLastSeenProducts') {
          let latestsViewed$ = this.localStorageService.getLastViewed();
          if (this.piece?.settings?.general?.sortDirection === 'oldFirst') {
            latestsViewed$ = latestsViewed$.pipe(map((products) => products.reverse()));
          }
          return latestsViewed$;
        }

        if (sourceType === 'mecaco') {
          return productsFromMecaco$;
        }

        return of([] as Product[]);
      }),
      map((products) => {
        if (this.piece?.settings?.general.displayFilterSeasonalProducts) {
          return this.filterSeasonalProducts(products);
        }
        return products;
      }),
      map((products = []) => products.slice(0, this.maxProducts)),
      tap((products) => {
        this.displayedProductIds = products.map((product) => product.id);
      })
    );

    this.breakpointObserver.observe(
      this.renderConfigService.config.breakpoints.map((breakpoint) => breakpoint.bp)
    ).pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe(() => {
      this.onMediaChange();
    });
  }

  public ngAfterViewInit(): void {
    combineLatest([
      this.store.select(selectIsModalOpen(`size-selector-modal-${this.piece?.id}`)),
      this.store.select(selectIsModalOpen(`add-to-cart-modal-${this.piece?.id}`))
    ]).pipe(takeUntil(this.clearSubscriptions$))
      .subscribe(([sizeModalOpen, addToCartModalOpen]: [boolean, boolean]) => {
        const isAnyModalOpen = sizeModalOpen || addToCartModalOpen;

        if (this.isMobile) {
          this.renderer.setStyle(document.documentElement, 'overflowY', isAnyModalOpen ? 'hidden' : 'auto');
        }

        this.zIndex = isAnyModalOpen ? '10' : '0';
      });
  }

  public ngOnChanges(): void {
    if (isDefined(this.piece) && isDefined(this.piece.settings) && isDefined(this.piece.settings.general)) {
      const settings = this.piece.settings.general;
      const styles = this.piece.settings.styles;
      const element = this.elementRef.nativeElement;

      if (isDefined(settings.sourceType)) {
        this.sourceType$.next(settings.sourceType);
      }

      if (isDefined(settings.sourceId)) {
        iif(
          () => settings.sourceType === 'seoId',
          this.getCategoryIdBySeoId$(settings.sourceId),
          of(settings.sourceId)
        ).subscribe((newSourceId) => this.sourceId$.next(newSourceId ?? null));
      }

      this.imageClass = `${PRODUCT_IMAGE_CLASS} ${this.imageBorderRadiusType}`;
      this.renderer.setStyle(element, '--image-border-radius', styles.imageBorderRadius, RendererStyleFlags2.DashCase);

      this.displayComponentArrows = settings.displayComponentArrows;
      this.displayComponentPager = settings.displayComponentPager;

      const { objectFit, objectPosition } = styles;

      if (!!objectFit) {
        this.renderer.setStyle(element, '--imageObjectFit', `${objectFit}`, 2);
      }

      if (!!objectPosition) {
        this.renderer.setStyle(element, '--imageObjectPosition', `${objectPosition}`, 2);
      }
    }
  }

  public get gridSize(): { numberOfColumns: number; numberOfRows: number } {
    if (this.piece?.settings?.general) {
      const gridSize = {
        numberOfColumns: +(this.piece.settings.general.numberOfColumns || PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS),
        numberOfRows: (this.piece.settings.general.numberOfRows || PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS)
      };

      if (this.isMobile) {
        if (this.piece.settings.general.numberOfColumnsMobile) {
          gridSize.numberOfColumns = this.piece.settings.general.numberOfColumnsMobile;
        }
        if (this.piece.settings.general.numberOfRowsMobile) {
          gridSize.numberOfRows = this.piece.settings.general.numberOfRowsMobile;
        }
      }
      return gridSize;
    }

    return {
      numberOfColumns: PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS,
      numberOfRows: PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS
    };
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.window.inditex?.notifications.clearHandlers(IPOC_PRODUCTS_INITIALIZED);
  }

  public get showSliderHeader(): boolean {
    let shouldShow = false;
    if (this.piece && this.piece.settings && this.piece.settings.general) {
      shouldShow = (this.piece.settings.general.showTitle
        && this.piece.settings.general.textGridTitle
        && this.piece.settings.general.textGridTitle.length > 0)
      || this.piece.settings.general.arrowPosition === 'header-right';
    }
    return shouldShow;
  }

  public isVisibleTitle(): boolean {
    return !!this.piece?.settings?.general?.showTitle;
  }

  public isVisibleName(): boolean {
    return !!this.piece?.settings?.general?.displayProductNames;
  }

  public isVisiblePrice(): boolean {
    return !!this.piece?.settings?.general?.displayProductPrices;
  }

  public isVisibleCart(): boolean {
    return !!this.piece?.settings?.general?.displayProductAddToCartToggle;
  }

  public isVisibleWishlist(): boolean {
    return !!this.piece?.settings?.general?.displayWishlist;
  }

  public isVisibleCartDeprecated(): boolean {
    return !!this.piece?.settings?.general?.displayProductAddToCart;
  }

  public isVisibleButtonLink(): boolean {
    return !!this.piece?.settings?.general?.displayButtonLink;
  }

  public isVisibleColors(): boolean {
    return !!this.piece?.settings?.general?.displayColors;
  }

  public getTextProductName(productName: string): string {
    if (this.piece?.settings?.general?.productNamesTextFormat) {
      const template = this.piece.settings.general.productNamesTextFormat;
      return template.replace(/{{productName}}/g, productName);
    }
    return PRODUCT_CAROUSEL_DEFAULT_PRODUCT_NAME_FORMAT.replace(/{{productName}}/g, productName);
  }

  public getProductImageData(product: Product): LazyDataInterface | undefined {
    const width = 600;
    const defaultImageLocation = 'category_page';
    const imageLocation = this.piece?.settings?.general.imageLocation || defaultImageLocation;
    const location: ImageLocation = imageLocation;

    const imageView = this.getViewStyle(this.piece?.settings?.general.viewStyle);

    const media = ProductsHelpers.firstImageForFirstColor(
      product.colors, location, width, undefined, undefined, imageView
    );
    const fallbackImage = this.window.inditex?.getStaticResourceUrl('images/placeholder.png');
    const src = media?.url ? media.url : media?.extraInfo?.deliveryUrl || fallbackImage;
    const productImageData: LazyDataInterface = { src, width: media?.size?.width, height: media?.size?.height };

    return productImageData;
  }

  public getProductLink(product: Product | ProductWithCategory, index: number): Link {
    const link: Link = {
      type: 'product',
      pieceName: this.piece?.name,
      productId: product.id,
      ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier }),
      ...(
        this.piece?.settings?.general.enableUnifiedUrl
        && { enableUnifiedUrl: this.piece.settings.general.enableUnifiedUrl }
      )
    };

    if ('categoryId' in product) {
      link.categoryId = product.categoryId;
    }

    if (product.productPartNumberDetails) {
      link.productPartNumberDetails = product.productPartNumberDetails;
    }

    if (EXTERNAL_SOURCES.includes(this.sourceType) && this.window.inditex?.iPOCRecommendationToken) {
      link.extraQueryParams = [{
        key: 'rtoken',
        value: `${this.window.inditex?.iPOCRecommendationToken}`
      }];
    }

    if (this.piece?.settings?.general?.salesAttribution) {
      link.extraQueryParams = [...link.extraQueryParams ?? [], {
        key: 'xs',
        value: `${this.piece?.settings?.general?.salesAttribution}`
      }];
    }

    return {
      ...link,
      productData: {
        index,
        product,
        sourceType: this.sourceType,
        displayedProductIds: this.displayedProductIds
      }
    };
  }

  public trackByProduct(index: number, item: Product): string {
    return item.id;
  }

  /** Tenants can invoke this method in their templates when they overwrite the Core template. */
  public onProductClick(event: Event, product: Product, index: number): void {
    const renderEvent = {
      event,
      extra: {
        type: 'productCarrouselSlideClicked',
        productId: product.id
      },
      data: {
        index,
        product,
        sourceType: this.sourceType,
        displayedProductIds: this.displayedProductIds,
        ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier })

      }
    } as RenderEvent;

    // TODO Remove support for categoryV2 https://axinic.central.inditex.grp/jira/browse/CMS-7284
    if (CATEGORIES_SOURCES.includes(this.sourceType)) {
      renderEvent.extra!.categoryId = this.piece?.settings?.general.sourceId;
    }

    this.renderEventsService.events.next(renderEvent);
  }

  public onProductLinkClick(product: Product, index: number): void {
    const productsWithImage = {
      ...product,
      imagePath: this.getProductImageData(product)?.src
    };
    const cmsEvent: CMSEvent<CMSProductCarouselEvent> = {
      name: 'select_item',
      type: 'click',
      origin: this.piece?.settings?.general?.origin || 'cross-custom-home',
      data: {
        index,
        product: productsWithImage,
        sourceType: 'ver_producto',
        linkType: 'foto',
        strategy: this.piece?.settings?.general?.strategy || this.strategy,
        provider: this.piece?.settings?.general?.provider || this.provider,
        objective: this.piece?.settings?.general?.objective || this.objective,
        ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier })
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public toggleWishlistFor(product: Product, index: number): void {
    let name: string;
    if (this.productsInWishlist.includes(product.id)) {
      this.productsInWishlist.splice(this.productsInWishlist.indexOf(product.id), 1);
      name = 'removeProductFromWishlist';
    } else {
      this.productsInWishlist.push(product.id);
      name = 'addProductToWishlist';
    }
    this.emitEvent(name, index, product);
  }

  public onFirstView(products: Product[]): void {
    if (!!products) {
      const productsWithImage = products.map((product) => ({
        ...product,
        imagePath: this.getProductImageData(product)?.src
      }));
      const cmsEvent: CMSEvent<CMSOnLoadProductsEvent> = {
        name: 'view_item_list',
        type: 'load',
        origin: this.piece?.settings?.general?.origin || 'cross-custom-home',
        data: {
          products: productsWithImage,
          size: products.length,
          viewType: this.gridSize.numberOfColumns,
          currency: this.currency,
          type: 'horizontal',
          strategy: this.piece?.settings?.general?.strategy || this.strategy,
          provider: this.piece?.settings?.general?.provider || this.provider,
          objective: this.piece?.settings?.general?.objective || this.objective
        }
      };
      this.cmsRenderEventsService.emitEvent(cmsEvent);
    }
  }

  public arrowClick(direction: 'prev' | 'next'): void {
    if (direction === 'next') {
      this.swiperContainer?.nativeElement.swiper.slideNext();
    } else {
      this.swiperContainer?.nativeElement.swiper.slidePrev();
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowRight':
        this.swiperContainer?.nativeElement.swiper.slideNext();
        this.updateFocusAfterSlideChange();
        event.preventDefault();
        break;
      case 'ArrowLeft':
        this.swiperContainer?.nativeElement.swiper.slidePrev();
        this.updateFocusAfterSlideChange();
        event.preventDefault();
        break;
      case 'Enter': {
        const target = event.target as HTMLElement;
        if (this.isImageFocused) {
          event.preventDefault();
          const renderLink = target.querySelector('a') as HTMLElement;
          // Simulate click on the product link
          renderLink?.click();
          this.isImageFocused = false;
        } else if (target.classList.contains('swiper-slide')) {
          // Focus on the product image
          event.preventDefault();
          this.lastFocusedElement = target;
          this.focusOnProductImage(target);
          this.isImageFocused = true;
        }
        break;
      }
      case 'Tab': {
        const target = event.target as HTMLElement;
        if (this.isImageFocused && target.closest('.product-card')) {
          event.preventDefault();
          const slideElement = target.closest('.swiper-slide') as HTMLElement;
          if (slideElement) {
            const productCard = slideElement.querySelector('.product-card');
            const index = parseInt(productCard?.getAttribute('data-index') || '0', 10);
            this.showSizeSelector(slideElement, index);
            this.isImageFocused = false;
          }
        }
        break;
      }
      case 'Escape': {
        this.isImageFocused = false;
        const target = event.target as HTMLElement;
        const slideElement = target.closest('.swiper-slide') as HTMLElement;
        if (slideElement) {
          slideElement.focus();
        }
        break;
      }
      default:
        break;
    }
  }

  private onMediaChange(): void {
    const gridSize = this.gridSize;
    this.swiperConfig = {
      ...this.swiperConfig,
      slidesPerView: gridSize.numberOfColumns,
      grid: {
        rows: gridSize.numberOfRows,
        fill: 'column'
      },
      slidesPerGroup: gridSize.numberOfColumns
    };
  }

  private emitEvent(name: CMSEvent<CMSProductCarouselEvent>['name'], index: number, product: Product): void {
    const cmsEvent: CMSEvent<CMSProductCarouselEvent> = {
      name,
      type: 'click',
      data: {
        index,
        product,
        sourceType: this.sourceType,
        ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier })
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  private externalProducts(): Observable<Product[]> {
    const hasIPOCProducts = (
      Array.isArray(this.window.inditex?.iPOCProducts) && this.window.inditex.iPOCProducts.length > 0
    );

    if (!hasIPOCProducts) {
      return of([] as Product[]);
    }

    return of(this.window.inditex!.iPOCProducts).pipe(
      filter((products) => Array.isArray(products)),
      map((products) => {
        const deduplicatedProductIds = Array.from(
          new Set(products!.map((product) => product.a_dataProduct.id.toString()))
        );
        this.provider = products?.[0].a_dataProduct.metadata?.provider ?? 'none';
        this.strategy = products?.[0].a_dataProduct.metadata?.strategy ?? 'manual';
        const reducedProductsIds = deduplicatedProductIds.slice(0, this.maxProducts);
        return this.sanitizeProducts(reducedProductsIds.join(','));
      }),
      switchMap((productIds: string) => {
        return this.productsService.productsDetailArray(
          productIds, undefined, undefined, this.configs()
        ).pipe(
          map((products: Product[]) => products.sort((a, b) => productIds.indexOf(a.id) - productIds.indexOf(b.id)))
        );
      })
    );
  }

  private sanitizeProducts(productsIds: string): string {
    return productsIds.replace(/\s/g, '');
  }

  private getViewStyle(viewStyle?: string): string[] {
    switch (viewStyle) {
      case 'silhouette-image':
        return ['s1', 's', 'c1', 'c'];
      case 'lookbook':
        return ['a10', 'c1', 'c'];
      case 'main':
        return ['a15', 'c1', 'c'];
      case 'custom-media-id':
        return this.piece?.settings?.general.customMediaId ? [this.piece.settings.general.customMediaId] : [];
      default:
        return []; // In this case return main medias
    }
  }

  private initConfig(): void {
    const styles = this.piece?.settings?.styles;
    const gridSize = this.gridSize;

    this.swiperConfig = {
      freeMode: this.isScrollFree,
      navigation: false,
      pagination: false,
      loop: this.isScrollPager && !!this.piece?.settings?.general.loop,
      spaceBetween: styles ? Number(styles.spaceBetweenProducts?.slice(0, -2) ?? 0) : undefined,
      slidesOffsetBefore: styles ? Number(styles.initialOffset?.slice(0, -2) ?? 0) : undefined,
      slidesOffsetAfter: styles ? Number(styles.finalOffset?.slice(0, -2) ?? 0) : undefined,
      slidesPerView: gridSize.numberOfColumns,
      grid: {
        rows: gridSize.numberOfRows,
        fill: 'column'
      },
      slidesPerGroup: gridSize.numberOfColumns,
      modules: [Pagination, Autoplay, Scrollbar, Grid, A11y, FreeMode]
    };

    if (this.isScrollPager && this.piece?.settings?.general.displayComponentPager) {
      this.swiperConfig.pagination = this.getPaginationOptions();
    }
  }

  private initTranslate(): void {
    const translations = this.renderSetupDataService.translations;
    if (this.piece?.settings?.general) {
      this.piece.settings.general.titleSize = translations?.['product-card.size'] ?? this.window.inditex?.text?.('sizes');
      this.piece.settings.general.titleSizeMobile = translations?.['product-card.size'] ?? this.window.inditex?.text?.('ItxProductSidebarComponent.size');
      this.piece.settings.general.oneSizeText = translations?.['product-card.unique-size'] ?? this.window.inditex?.text?.('ItxProductGridComponent.uniqueSize');
      this.piece.settings.general.petiteSizeText = translations?.['product-card.short-size-type'] ?? this.window.inditex?.text?.('GenericMessage.sizeTypeShort');
      this.piece.settings.general.petiteSizeDescription = translations?.['product-card.short-size-type-description'] ?? this.window.inditex?.text?.('ItxProductPage.shorter');
      this.piece.settings.general.regularSizeText = translations?.['product-card.normal-size-type'] ?? this.window.inditex?.text?.('GenericMessage.sizeTypeRegular');
      this.piece.settings.general.regularSizeDescription = translations?.['product-card.normal-size-type-description'] ?? this.window.inditex?.text?.('ItxProductPage.sameLength');
      this.piece.settings.general.tallSizeText = translations?.['product-card.long-size-type'] ?? this.window.inditex?.text?.('GenericMessage.sizeTypeLong');
      this.piece.settings.general.tallSizeDescription = translations?.['product-card.long-size-type-description'] ?? this.window.inditex?.text?.('ItxProductPage.longer');
      this.piece.settings.general.modelHeight = translations?.['product-card.model-height'] ?? this.window.inditex?.text?.('GenericMessage.modelHeight');
      this.piece.settings.general.modelSize = translations?.['product-card.model-size'] ?? this.window.inditex?.text?.('GenericMessage.modelSize');
      this.piece.settings.general.backSoonText = translations?.['product-card.notify-me'] ?? this.window.inditex?.text?.('ItxProductPage.notifyMe');
    }
  }

  private filterSeasonalProducts(products: Product[]): Product[] {
    const regex = /-(.)/;
    const seasonalProducts = [];
    for (const product of products) {
      const match = product.fullReference?.match(regex);
      const season = match ? match[1] : null;
      if (season === this.piece?.settings?.general.filterSeasonalProducts) {
        seasonalProducts.push(product);
      }
    }
    return seasonalProducts;
  }

  private getPaginationOptions(): PaginationOptions {
    let bulletClass = 'swiper-pagination-bullet';
    if (this.isPagerStyleBox) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-box`;
    } else if (this.isPagerStyleSolid) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-solid`;
    } else if (this.isPagerStyleDashed) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-dashed`;
    }
    return {
      clickable: true,
      renderBullet: () => {
        return `<button class="${bulletClass}"></button>`;
      }
      // Here the `el` property is needed but it does not appear at the very beginning of the component initialization,
      // that's why it will be read in the directive swiper [cmsSwiper]
    };
  }

  private getCategoryIdBySeoId$(seoId: string): Observable<string | undefined> {
    return this.contextService.getCategories$().pipe(
      map((categories) => CategoriesHelpers.flatCategories(categories)),
      map((categories) => categories.find((category) => category.seoId === seoId)?.id)
    );
  }

  private showSizeSelector(slideElement: HTMLElement, productIndex: number): void {
    this.products$.pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe((products) => {
      if (!products || productIndex >= products.length) {
        return;
      }

      const imageProduct = slideElement.querySelector('.image-product');
      const purchaseOptions = imageProduct?.querySelector('render-product-purchase-options') as HTMLElement;

      if (purchaseOptions) {
        purchaseOptions.style.display = 'block';
        this.setupSizeSelectorNavigation(purchaseOptions);
      }
    });
  }

  private setupSizeSelectorNavigation(selector: HTMLElement): void {
    const interactiveElements: HTMLElement[] = Array.from(selector.querySelectorAll(
      'button:not([disabled]), select:not([disabled]), a:not([disabled])'
    ));

    if (interactiveElements.length === 0) {
      return;
    }

    interactiveElements.forEach((el) => {
      el.setAttribute('tabindex', '0');
    });

    interactiveElements[0].focus();
  }

  private focusOnProductImage(slideElement: HTMLElement): void {
    const imageProduct = slideElement.querySelector('.image-product render-link') as HTMLElement;
    if (imageProduct) {
      imageProduct.setAttribute('tabindex', '0');
      imageProduct.focus();

      const removeOutline = () => {
        imageProduct.removeAttribute('tabindex');
        imageProduct.removeEventListener('blur', removeOutline);
      };

      imageProduct.addEventListener('blur', removeOutline);
    }
  }

  private updateFocusAfterSlideChange(): void {
    if (!this.swiperContainer?.nativeElement?.swiper) {
      return;
    }
    this.isImageFocused = false;

    setTimeout(() => {
      const activeIndex = this.swiperContainer!.nativeElement.swiper.activeIndex;
      const slidesArray = this.swiperSlides.toArray();

      if (activeIndex < slidesArray.length) {
        const activeSlide = slidesArray[activeIndex].nativeElement;
        activeSlide.focus();
      }
    }, 300);
  }

}
